/**
 * My Subscription — clinic-facing self-service page (v2 tab layout).
 *
 * Horizontal tab strip mirroring the Inventory module style
 * (border-b-2 indigo underline · uppercase tracking-wider · white active
 * background). Four tabs:
 *
 *   1. Overview        — live plan card + countdown + status-aware CTA
 *   2. Features        — checklist of what's in your current tier + what
 *                        needs Chain, with Downgrade CTA for Chain owners
 *   3. What's New      — hard-coded changelog with a "New" badge that
 *                        clears on first visit (per-user localStorage)
 *   4. Invoice History — the full paid + refunded + pending table with
 *                        Razorpay refs and (when available) a PDF button
 *
 * Marked `data-readonly-safe="true"` so lapsed tenants can still hit
 * the Renew / Pay / Downgrade CTAs on this page.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Receipt, Sparkles, ShieldCheck, Calendar, ArrowDownRight, AlertTriangle,
  Check, X, Rocket, FileText, Download, RefreshCcw, ArrowRight, CircleAlert,
} from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { useSubscription } from '../../SubscriptionContext';
import { RazorpayPayTenantInvoiceButton, RazorpayReconcileButton } from '../admin/panel/RazorpayTenantInvoiceActions';
import ChangePlanModal from './ChangePlanModal';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const STATUS_TONES = {
  trialing:          { pill: 'bg-indigo-100 text-indigo-800 border-indigo-200',   label: 'Trialing' },
  active:            { pill: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Active' },
  grace:             { pill: 'bg-amber-100 text-amber-800 border-amber-200',       label: 'Grace' },
  expired:           { pill: 'bg-rose-100 text-rose-800 border-rose-200',           label: 'Expired' },
  cancelled:         { pill: 'bg-slate-200 text-slate-700 border-slate-300',        label: 'Cancelled' },
  pending_selection: { pill: 'bg-amber-100 text-amber-800 border-amber-200',       label: 'Pending' },
};

const INVOICE_STATUS_TONES = {
  pending:            'bg-amber-50 text-amber-800 border-amber-200',
  paid:               'bg-emerald-50 text-emerald-800 border-emerald-200',
  refunded:           'bg-rose-50 text-rose-800 border-rose-200',
  partially_refunded: 'bg-amber-50 text-amber-800 border-amber-200',
  cancelled:          'bg-slate-100 text-slate-600 border-slate-200',
};

const CLINIC_FEATURES = [
  { key: 'front_desk',       label: 'Front desk · queue · patient portal', in_clinic: true, in_chain: true },
  { key: 'diagnostics',      label: 'Full audiometry suite (PT · Impedance · Speech · OAE · ABR · Paediatric)', in_clinic: true, in_chain: true },
  { key: 'ha_sales',         label: 'Hearing-aid sales · trials · quotations · fittings', in_clinic: true, in_chain: true },
  { key: 'ha_amc',           label: 'AMC / warranty / follow-up tracking',   in_clinic: true, in_chain: true },
  { key: 'gst_invoicing',    label: 'GST invoicing with HSN + sequential numbering', in_clinic: true, in_chain: true },
  { key: 'whatsapp_reminders', label: 'WhatsApp / SMS reminders (via MSG91 · Twilio · Resend)', in_clinic: true, in_chain: true },
  { key: 'basic_reporting',  label: 'Basic reports · revenue · patients · staff activity', in_clinic: true, in_chain: true },
  { key: 'two_clinics',      label: '2 clinics included (Clinic) · 4 clinics included (Chain)', in_clinic: true, in_chain: true },
];

const CHAIN_ONLY_FEATURES = [
  { key: 'cross_branch_analytics', label: 'Cross-branch analytics dashboard' },
  { key: 'inventory_transfers',    label: 'Inventory transfers between branches' },
  { key: 'stock_heatmap',          label: 'Stock heatmap · live rebalancing view' },
  { key: 'scheduled_reports',      label: 'Scheduled email reports (daily · weekly · monthly)' },
  { key: 'consolidated_pnl',       label: 'Consolidated multi-branch P&L' },
];

// Hardcoded changelog — newest first. Neo edits this list going forward.
// `id` is the release key used to compute the "New" badge (per user).
const CHANGELOG = [
  {
    id: '2026-09-01', version: 'v2.6.0', date: '1 Sep 2026',
    title: 'Trial extension & self-healing subscriptions',
    entries: [
      "Every existing tenant gets a fresh 30-day trial — status auto-flipped to Trialing with 30 days left.",
      "New signups automatically land on a 30-day Chain trial — no card required for the first month.",
      "Read-only banner regressions are self-healed at read time so no one gets locked out due to a missing subscription row.",
    ],
  },
  {
    id: '2026-08-31-po', version: 'v2.5.0', date: '31 Aug 2026',
    title: 'Founder Private Onboarding + Downgrade with prorated refund',
    entries: [
      "Chain → Clinic downgrade with a 15-day export window and an automatic prorated refund calculated on your paid days.",
      "Founder-only panel to mint custom-priced Razorpay links for hand-picked early customers (with full audit trail).",
      "Read-only tooltip on every save button when your subscription lapses — no more surprise 403s mid-flow.",
    ],
  },
  {
    id: '2026-08-31-v2', version: 'v2.4.0', date: '31 Aug 2026',
    title: 'Subscription V2 · Clinic & Chain tiers launched',
    entries: [
      "Two clean tiers: Clinic (₹1,299/month, up to 2 clinics) and Chain (₹1,799/month, up to 4 clinics + premium features).",
      "Four billing cycles — Monthly · Quarterly (5% off) · Half-Yearly (10% off) · Annual (30% off · LAUNCH OFFER).",
      "GST-compliant subscription invoices with sequential SUB-* numbering, GSTIN, place of supply and CGST/SGST/IGST split.",
      "Add extra clinics anytime at ₹499 / clinic / month.",
    ],
  },
  {
    id: '2026-08-30-reports', version: 'v2.3.0', date: '30 Aug 2026',
    title: 'Scheduled email reports (Chain plan)',
    entries: [
      "Configure daily / weekly / monthly reports and get them delivered to your inbox automatically.",
      "Live activity heartbeat plus improved reliability of the daily report cron.",
    ],
  },
  {
    id: '2026-08-28-heatmap', version: 'v2.2.0', date: '28 Aug 2026',
    title: 'Stock heatmap + cross-branch transfers (Chain plan)',
    entries: [
      "Rebalance inventory across your clinics from one heatmap view.",
      "One-click stock transfer workflow between branches with a challan document.",
    ],
  },
];

// Order matters — index used to compute active tab.
const TABS = [
  { key: 'overview',  label: 'Overview',        testid: 'my-sub-tab-overview' },
  { key: 'features',  label: 'Features',        testid: 'my-sub-tab-features' },
  { key: 'whats_new', label: "What's New",      testid: 'my-sub-tab-whats-new' },
  { key: 'invoices',  label: 'Invoice History', testid: 'my-sub-tab-invoices' },
];


export default function MySubscriptionPage() {
  const { user } = useAuth();
  const sub = useSubscription();
  const [tab, setTab] = useState('overview');
  const [pending, setPending] = useState([]);
  const [paid,    setPaid]    = useState([]);
  const [other,   setOther]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [downgradeOpen, setDowngradeOpen] = useState(false);

  const isOwner = user && ['clinic_owner', 'super_admin', 'founder'].includes(user.role);

  // "What's New" — per-user seen-marker in localStorage. The badge shows
  // when the newest changelog id differs from the stored last-seen id.
  const seenKey = user ? `audinexa.whats_new_seen.${user.user_id || user.email}` : 'audinexa.whats_new_seen';
  const [seenId, setSeenId] = useState(() => {
    try { return localStorage.getItem(seenKey) || ''; } catch { return ''; }
  });
  const latestId = CHANGELOG[0]?.id;
  const hasNewChangelog = latestId && seenId !== latestId;

  // Mark the changelog as seen when the tab opens.
  useEffect(() => {
    if (tab !== 'whats_new' || !hasNewChangelog) return;
    try {
      localStorage.setItem(seenKey, latestId);
      setSeenId(latestId);
    } catch {}
  }, [tab, hasNewChangelog, latestId, seenKey]);

  const load = useCallback(async () => {
    if (!isOwner) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/subscription/invoices`);
      setPending(r.data.pending || []);
      setPaid(r.data.paid || []);
      setOther(r.data.other || []);
    } catch {
      setPending([]); setPaid([]); setOther([]);
    } finally { setLoading(false); }
  }, [isOwner]);
  useEffect(() => { load(); }, [load]);

  // Auto-refresh subscription context when tab opens — catches any
  // just-completed payment webhook that arrived while the user was
  // reading the Overview page.
  useEffect(() => {
    if (tab === 'overview' && sub?.refresh) sub.refresh();
  }, [tab]);   // eslint-disable-line react-hooks/exhaustive-deps

  const onPaid = (updated) => {
    if (sub?.refresh) sub.refresh();
    load();
    if (updated?.invoice_id) {
      setTimeout(() => {
        // eslint-disable-next-line no-alert
        alert(`Payment received! Invoice ${updated.invoice_id} marked paid. Your plan is now active.`);
      }, 200);
    }
  };

  if (!isOwner) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="my-subscription-not-owner">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-sm text-amber-900">
          Subscription billing is only visible to the clinic owner. Please ask
          your clinic owner to manage subscription payments.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-5xl" data-testid="my-subscription-page" data-readonly-safe="true">
      <header>
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Receipt size={20} className="text-indigo-600" /> My Subscription
        </h1>
        <p className="text-[12.5px] text-slate-500 mt-0.5">
          Your AUDINEXA plan, features, product updates and invoice history.
        </p>
      </header>

      {/* ─── Horizontal tab strip (Inventory-module style) ─── */}
      <div className="border-b border-slate-200 flex overflow-x-auto" role="tablist" data-testid="my-sub-tabs">
        {TABS.map((t) => {
          const isActive = tab === t.key;
          const showBadge = t.key === 'whats_new' && hasNewChangelog;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              role="tab"
              aria-selected={isActive}
              data-testid={t.testid}
              className={`relative px-4 py-2 text-[12px] font-semibold uppercase tracking-wider border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                isActive
                  ? 'border-indigo-600 text-indigo-700 bg-white'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
              {showBadge && (
                <span
                  data-testid="my-sub-whats-new-badge"
                  className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold uppercase tracking-wider"
                >
                  New
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && (
        <OverviewTab
          sub={sub}
          onDowngrade={() => setDowngradeOpen(true)}
          pending={pending}
          onPaid={onPaid}
        />
      )}
      {tab === 'features' && (
        <FeaturesTab sub={sub} onDowngrade={() => setDowngradeOpen(true)} />
      )}
      {tab === 'whats_new' && (
        <WhatsNewTab />
      )}
      {tab === 'invoices' && (
        <InvoicesTab pending={pending} paid={paid} other={other} loading={loading} onPaid={onPaid} />
      )}

      <ChangePlanModal
        open={downgradeOpen}
        onClose={() => setDowngradeOpen(false)}
      />
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Tab 1 · Overview
// ────────────────────────────────────────────────────────────────────
function OverviewTab({ sub, onDowngrade, pending, onPaid }) {
  const v2 = sub?.v2 || {};
  const tierName = { chain: 'Chain', clinic: 'Clinic', enterprise: 'Enterprise' }[v2.tier] || 'Not selected';
  const status = v2.status || 'pending_selection';
  const tone = STATUS_TONES[status] || STATUS_TONES.pending_selection;

  const trialStart = null;   // provisional — trial_started_at not in v2 payload today
  const currentPeriodStart = v2.current_period_start || null;
  const currentPeriodEnd   = v2.current_period_end   || null;
  const graceEnds          = v2.grace_ends_at        || null;
  const daysLeft = v2.days_left;

  // Prominent status line copy.
  let countdown = null;
  let countdownDetail = null;
  if (status === 'trialing') {
    countdown = `${daysLeft ?? 0} day${daysLeft === 1 ? '' : 's'} left in trial`;
    countdownDetail = currentPeriodEnd
      ? `Trial ends ${fmtDate(currentPeriodEnd)}`
      : null;
  } else if (status === 'active') {
    countdown = `Next renewal on ${fmtDate(currentPeriodEnd)}`;
    countdownDetail = currentPeriodStart
      ? `Last renewed on ${fmtDate(currentPeriodStart)}`
      : null;
  } else if (status === 'grace') {
    countdown = `${daysLeft ?? 0} day${daysLeft === 1 ? '' : 's'} until expiry`;
    countdownDetail = graceEnds ? `Grace ends ${fmtDate(graceEnds)}` : null;
  } else if (status === 'expired') {
    countdown = 'Subscription lapsed — read-only';
    countdownDetail = 'Renew to restore create / write access.';
  } else if (status === 'cancelled') {
    countdown = `Access continues until ${fmtDate(currentPeriodEnd)}`;
    countdownDetail = 'Your subscription is cancelled but stays live until period end.';
  } else if (status === 'pending_selection') {
    countdown = 'Choose your plan to continue';
    countdownDetail = null;
  }

  return (
    <div className="space-y-4">
      {/* Plan card */}
      <section
        className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-indigo-50/40 p-5 shadow-sm"
        data-testid="my-sub-overview-card"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.2em] font-bold text-slate-500">
              Current plan
              <span
                data-testid="my-sub-status-badge"
                className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${tone.pill}`}
              >
                {tone.label}
              </span>
            </div>
            <div className="mt-1 text-4xl font-extrabold text-slate-900 tracking-tight" style={{ letterSpacing: '-0.03em' }}>
              {tierName}
            </div>
            <div className="mt-2 text-[13.5px] font-semibold text-slate-800" data-testid="my-sub-countdown">
              {countdown}
            </div>
            {countdownDetail && (
              <div className="text-[11.5px] text-slate-500 mt-0.5" data-testid="my-sub-countdown-detail">
                {countdownDetail}
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <OverviewCTA sub={sub} pending={pending} onPaid={onPaid} />
            {status === 'active' && v2.tier !== 'enterprise' && (
              <button onClick={onDowngrade} data-testid="my-sub-change-plan-btn"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[12.5px] font-semibold rounded-lg border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50 transition-colors">
                <Sparkles size={13} /> Change plan
              </button>
            )}
          </div>
        </div>

        {/* Clinic quota */}
        {v2.tier && (
          <div className="mt-5 pt-4 border-t border-slate-200/70 grid grid-cols-2 sm:grid-cols-4 gap-4 text-[12px]">
            <Stat label="Locations"        value={v2.location_limit ?? '—'} />
            <Stat label="Users"            value={v2.user_limit ?? '—'} />
            {(v2.extra_clinics_count > 0 || v2.extra_users_count > 0) && (
              <Stat label="Add-ons" value={`${v2.extra_clinics_count || 0} loc · ${v2.extra_users_count || 0} users`} />
            )}
            {v2.founding_member && (
              <Stat label="Founding member" value={`₹${Number(v2.locked_price || 0).toLocaleString('en-IN')} locked`} />
            )}
            <Stat label="Billing cycle"    value={v2.billing_cycle ? v2.billing_cycle.replace('_', ' ') : '—'} capitalize />
            {status === 'active' && (
              <Stat label="Last renewed"  value={fmtDate(currentPeriodStart)} />
            )}
            {status === 'trialing' && (
              <Stat label="Trial started" value={fmtDate(currentPeriodStart)} />
            )}
          </div>
        )}

        {v2.downgrade_scheduled && (
          <div
            className="mt-4 rounded-lg border border-violet-200 bg-violet-50 px-3.5 py-2.5 flex items-start gap-2 text-[12px] text-violet-900"
            data-testid="my-sub-downgrade-chip"
          >
            <ArrowDownRight size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>Downgrade scheduled</b> — you switch to the <b className="capitalize">{v2.downgrade_scheduled.to}</b> plan on <b>{fmtDate(v2.downgrade_scheduled.at)}</b>
              {v2.downgrade_scheduled.blocked && <> · <b>waiting</b> until users/locations fit the new plan</>}.
              <button
                onClick={onDowngrade}
                data-testid="my-sub-manage-downgrade"
                className="ml-2 underline font-semibold"
              >
                Manage
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Pending invoice quick-pay strip (kept on Overview so tenants can
          settle a due amount without hopping tabs). Full history lives on
          the Invoice History tab. */}
      {pending.length > 0 && (
        <section
          className="rounded-xl border border-amber-200 bg-amber-50 p-4"
          data-testid="my-sub-overview-pending"
        >
          <div className="flex items-start gap-3">
            <span className="w-8 h-8 rounded-lg bg-amber-200 text-amber-800 flex items-center justify-center shrink-0">
              <AlertTriangle size={16} />
            </span>
            <div className="flex-1">
              <div className="text-[13px] font-bold text-slate-900">
                {pending.length} invoice{pending.length === 1 ? '' : 's'} pending · pay to renew
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {pending.map((inv) => (
                  <PendingInvoiceCard key={inv.invoice_id} inv={inv} onPaid={onPaid} />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Reassuring "all paid up" state (only when everything is green) */}
      {status === 'active' && pending.length === 0 && (
        <section
          className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3 text-[12.5px] text-emerald-800"
          data-testid="my-sub-all-paid"
        >
          <ShieldCheck size={16} className="text-emerald-600 shrink-0" />
          <div>
            <b>You&apos;re all paid up.</b> Next invoice on <b>{fmtDate(currentPeriodEnd)}</b>.
          </div>
        </section>
      )}
    </div>
  );
}

function OverviewCTA({ sub, pending, onPaid }) {
  const v2 = sub?.v2 || {};
  const status = v2.status;

  // pending_selection or trialing — big "Choose a plan and pay" opens
  // the shell-level PlanSelectionModal by nudging the tenant to /billing
  // (modal is shell-mounted; if status is pending_selection it's already
  // covering the page). For trialing we route to /billing/pay-now which
  // triggers the same modal via a status forcing helper on load, OR the
  // pending-invoice quick-pay if there's a live invoice.
  if (status === 'trialing') {
    return (
      <ChooseAndPayCTA
        label={`Choose a plan and pay · ${v2.days_left ?? 0} day${v2.days_left === 1 ? '' : 's'} of trial left`}
        testid="my-sub-choose-plan-btn"
      />
    );
  }
  if (status === 'grace') {
    // If there's a live pending invoice, big Renew button pays it.
    const first = pending[0];
    if (first) {
      return (
        <div data-testid="my-sub-renew-cta" className="flex flex-col items-end gap-1">
          <RazorpayPayTenantInvoiceButton invoice={first} onPaid={onPaid} />
          <div className="text-[10.5px] text-slate-500">Grace · {v2.days_left ?? 0} d left</div>
        </div>
      );
    }
    return (
      <ChooseAndPayCTA
        label="Renew now"
        testid="my-sub-renew-cta"
      />
    );
  }
  if (status === 'expired') {
    return (
      <ChooseAndPayCTA
        label="Renew to restore access"
        testid="my-sub-restore-cta"
        tone="rose"
        nonDismissible
      />
    );
  }
  if (status === 'active') {
    return (
      <div
        data-testid="my-sub-active-cta"
        className="text-right text-[12.5px] text-emerald-700 font-semibold inline-flex items-center gap-1.5"
      >
        <ShieldCheck size={14} /> Next invoice on {fmtDate(v2.current_period_end)}
      </div>
    );
  }
  return null;
}

function ChooseAndPayCTA({ label, testid, tone = 'indigo', nonDismissible }) {
  // The plan-selection modal is shell-mounted and shows automatically
  // when status=pending_selection. For trialing/grace/expired we route
  // to /billing (where the modal auto-opens on any status via a small
  // helper we set on window). Simplest: reload with a hash flag that
  // the PlanSelectionModal reads to force-open even when status is not
  // pending_selection.
  const trigger = () => {
    // Broadcast a custom event the PlanSelectionModal can subscribe to.
    // If the modal isn't listening (older bundle), a hard fallback of
    // navigating to /#pricing keeps the CTA useful.
    try {
      window.dispatchEvent(new CustomEvent('audinexa:open-plan-modal'));
    } catch {}
    // Fallback for tenants whose subscription is legitimately in
    // pending_selection — modal is already open, this is a no-op.
  };
  const cls = tone === 'rose'
    ? 'bg-rose-600 hover:bg-rose-700 text-white'
    : 'bg-indigo-600 hover:bg-indigo-700 text-white';
  return (
    <button
      onClick={trigger}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-bold rounded-lg shadow-sm ${cls} ${nonDismissible ? 'ring-2 ring-rose-300 animate-pulse' : ''}`}
    >
      <Sparkles size={14} /> {label} <ArrowRight size={14} />
    </button>
  );
}


// ────────────────────────────────────────────────────────────────────
// Tab 2 · Features
// ────────────────────────────────────────────────────────────────────
function FeaturesTab({ sub, onDowngrade }) {
  const v2 = sub?.v2 || {};
  const tier = v2.tier;
  const isChain = tier === 'chain' || tier === 'enterprise';

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="my-sub-features-included">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-[13px] font-bold text-slate-900">
            Included in your {tier ? ({ chain: 'Chain', clinic: 'Clinic', enterprise: 'Enterprise' }[tier]) : 'current'} plan
          </div>
          <div className="text-[10.5px] text-slate-500 uppercase tracking-wider font-bold">
            {isChain ? `${CLINIC_FEATURES.length + CHAIN_ONLY_FEATURES.length}` : CLINIC_FEATURES.length} features
          </div>
        </div>
        <ul className="divide-y divide-slate-100">
          {CLINIC_FEATURES.map((f) => (
            <FeatureRow key={f.key} label={f.label} included data-testid={`feature-${f.key}`} />
          ))}
          {isChain && CHAIN_ONLY_FEATURES.map((f) => (
            <FeatureRow key={f.key} label={f.label} included highlight data-testid={`feature-${f.key}`} />
          ))}
        </ul>
      </section>

      {!isChain && (
        <section className="rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden" data-testid="my-sub-features-chainonly">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-[13px] font-bold text-slate-500">Available on Chain plan</div>
            <a
              href="/#pricing"
              target="_blank"
              rel="noreferrer"
              className="text-[11.5px] text-indigo-600 hover:underline font-semibold inline-flex items-center gap-1"
              data-testid="my-sub-see-chain-cta"
            >
              See Chain plan <ArrowRight size={11} />
            </a>
          </div>
          <ul className="divide-y divide-slate-100">
            {CHAIN_ONLY_FEATURES.map((f) => (
              <FeatureRow
                key={f.key}
                label={f.label}
                included={false}
                muted
                trailing={
                  <span className="text-[10.5px] font-bold uppercase tracking-wider text-indigo-600">
                    Chain
                  </span>
                }
                data-testid={`feature-locked-${f.key}`}
              />
            ))}
          </ul>
        </section>
      )}

      {isChain && (
        <div className="flex items-center justify-end pt-2">
          <button
            onClick={onDowngrade}
            data-testid="my-sub-features-downgrade-btn"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[12.5px] font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          >
            <ArrowDownRight size={14} /> Change or downgrade plan
          </button>
        </div>
      )}
    </div>
  );
}

function FeatureRow({ label, included, highlight, muted, trailing, ...rest }) {
  return (
    <li className="px-5 py-2.5 flex items-center gap-3 text-[13px]" {...rest}>
      <span className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 ${
        included
          ? (highlight ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700')
          : 'bg-slate-200 text-slate-400'
      }`}>
        {included ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />}
      </span>
      <span className={`flex-1 ${muted ? 'text-slate-500 line-through decoration-slate-300' : 'text-slate-800'}`}>
        {label}
      </span>
      {trailing}
    </li>
  );
}


// ────────────────────────────────────────────────────────────────────
// Tab 3 · What's New
// ────────────────────────────────────────────────────────────────────
function WhatsNewTab() {
  return (
    <div className="space-y-4" data-testid="my-sub-whats-new-list">
      {CHANGELOG.map((r) => (
        <section key={r.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`changelog-${r.id}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-slate-500">
                {r.version} · {r.date}
              </div>
              <div className="mt-1 text-[15px] font-bold text-slate-900">{r.title}</div>
            </div>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10.5px] font-semibold">
              <Rocket size={11} /> release
            </span>
          </div>
          <ul className="mt-3 space-y-1.5">
            {r.entries.map((e, idx) => (
              <li key={idx} className="flex items-start gap-2 text-[13px] text-slate-700">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-2 shrink-0" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <p className="text-[11px] text-slate-400 leading-relaxed">
        New updates are added here as we ship them. Have a request?
        Email <a href="mailto:hello@audinexa.com" className="text-indigo-600 underline">hello@audinexa.com</a>.
      </p>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────
// Tab 4 · Invoice History
// ────────────────────────────────────────────────────────────────────
function InvoicesTab({ pending, paid, other, loading, onPaid }) {
  const rows = useMemo(
    () => [...pending, ...paid, ...other],
    [pending, paid, other],
  );
  const totalCount = rows.length;

  return (
    <div className="space-y-4">
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="my-sub-invoices-table">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[13px] font-bold text-slate-900">Invoice history</div>
            <div className="text-[11px] text-slate-500">All AUDINEXA invoices — pending, paid, and refunded</div>
          </div>
          <div className="text-[11px] text-slate-500">{totalCount} record{totalCount === 1 ? '' : 's'}</div>
        </div>
        {loading ? (
          <div className="px-4 py-12 text-center italic text-slate-400 text-sm">Loading invoices…</div>
        ) : totalCount === 0 ? (
          <div className="px-4 py-12 text-center italic text-slate-400 text-sm">
            No invoices yet. Your first AUDINEXA invoice will appear here once we issue it.
          </div>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-[12.5px]">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] font-bold tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5">Invoice</th>
                  <th className="text-left px-4 py-2.5">Plan</th>
                  <th className="text-left px-4 py-2.5">Cycle</th>
                  <th className="text-left px-4 py-2.5">Issued</th>
                  <th className="text-right px-4 py-2.5">Amount</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Reference</th>
                  <th className="text-right px-4 py-2.5 w-[80px]">PDF</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => (
                  <InvoiceRow key={i.invoice_id} inv={i} onPaid={onPaid} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {pending.length > 0 && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="my-sub-invoices-pending-cta">
          <div className="text-[13px] font-bold text-slate-900 mb-2">Pay pending invoices</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {pending.map((inv) => (
              <PendingInvoiceCard key={inv.invoice_id} inv={inv} onPaid={onPaid} />
            ))}
          </div>
        </section>
      )}

      <p className="text-[10.5px] text-slate-400 leading-relaxed">
        Need help with billing? Email <a href="mailto:billing@audinexa.com" className="text-indigo-600 underline">billing@audinexa.com</a>.
        Refunds are processed via the original payment method within 5–7 business days as per our
        <a href="/refund" target="_blank" rel="noreferrer" className="text-indigo-600 underline ml-1">Refund Policy</a>.
      </p>
    </div>
  );
}

function InvoiceRow({ inv, onPaid }) {
  const tone = INVOICE_STATUS_TONES[inv.status] || INVOICE_STATUS_TONES.cancelled;
  const cycleLabel = (inv.cycle_label || inv.duration || '').replace('_', ' ');
  const tierLabel = inv.tier_label || inv.tier || '—';
  const pdfHref = inv.invoice_id ? `/billing/subscription-invoice/${inv.invoice_id}` : null;
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`my-sub-invoice-row-${inv.invoice_id}`}>
      <td className="px-4 py-2.5">
        <div className="font-mono text-[11px] text-slate-500">{inv.invoice_id}</div>
        {inv.invoice_no && <div className="text-[11.5px] font-semibold text-slate-800">{inv.invoice_no}</div>}
      </td>
      <td className="px-4 py-2.5 capitalize">{String(tierLabel).toLowerCase()}</td>
      <td className="px-4 py-2.5 capitalize">{cycleLabel || '—'}</td>
      <td className="px-4 py-2.5 text-slate-600">{fmtDate(inv.created_at || inv.issued_at)}</td>
      <td className="px-4 py-2.5 text-right tabular-nums font-semibold">
        {fmtINR(inv.grand_total || inv.total_amount || inv.amount)}
      </td>
      <td className="px-4 py-2.5">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10.5px] font-semibold ${tone}`}>
          {String(inv.status || '').replace(/_/g, ' ')}
        </span>
        {inv.refunded_total > 0 && (
          <div className="text-[10px] text-rose-600 mt-0.5">Refunded {fmtINR(inv.refunded_total)}</div>
        )}
      </td>
      <td className="px-4 py-2.5 text-[11px] text-slate-500 font-mono">
        {inv.razorpay_payment_id || inv.payment_ref || '—'}
      </td>
      <td className="px-4 py-2.5 text-right">
        {pdfHref ? (
          <Link
            to={pdfHref}
            data-testid={`my-sub-invoice-pdf-${inv.invoice_id}`}
            className="inline-flex items-center gap-1 px-2 py-1 text-[10.5px] font-semibold text-indigo-600 hover:bg-indigo-50 rounded-md"
            title="View / print GST invoice"
          >
            <Download size={11} /> Invoice
          </Link>
        ) : (
          <span className="text-[10.5px] text-slate-300">—</span>
        )}
      </td>
    </tr>
  );
}


// ────────────────────────────────────────────────────────────────────
// Shared bits
// ────────────────────────────────────────────────────────────────────
function Stat({ label, value, suffix, capitalize }) {
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
      <div className={`mt-0.5 text-[15px] font-bold text-slate-900 ${capitalize ? 'capitalize' : ''}`}>
        {value}{suffix && <span className="ml-1 text-[10.5px] font-medium text-slate-500">{suffix}</span>}
      </div>
    </div>
  );
}

function PendingInvoiceCard({ inv, onPaid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-col gap-2" data-testid={`my-sub-pending-${inv.invoice_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12.5px] font-bold text-slate-900 capitalize">
            {(inv.tier_label || inv.tier || '').toLowerCase()} · {(inv.cycle_label || inv.duration || '').replace('_', ' ')}
          </div>
          <div className="text-[10.5px] text-slate-500 font-mono truncate">{inv.invoice_id}</div>
        </div>
        <div className="text-right">
          <div className="text-base font-black tabular-nums text-slate-900">
            {fmtINR(inv.grand_total || inv.total_amount || inv.amount)}
          </div>
          <div className="text-[10px] text-slate-500">incl. 18% GST</div>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
        <div className="text-[10.5px] text-slate-500">Due {fmtDate(inv.due_date) || 'on receipt'}</div>
        <div className="inline-flex items-center gap-1.5 flex-wrap justify-end">
          <RazorpayReconcileButton invoice={inv} onReconciled={onPaid} />
          <RazorpayPayTenantInvoiceButton invoice={inv} onPaid={onPaid} />
        </div>
      </div>
    </div>
  );
}
